/**
 * Guardian Home Page
 *
 * Dashboard for guardians/parents showing their linked athletes.
 * Provides quick access to view athlete videos and progress.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listFamilyLinks,
  listChallenges,
  type Organization,
  type FamilyLink,
  type Challenge,
} from '@tss/shared';
import {
  Users,
  Video,
  Bell,
  ChevronRight,
  UserPlus,
  Heart,
  Trophy,
  Flame,
  Star,
} from 'lucide-react';
import { EmptyState } from '@tss/shared/ui';
import { AnnouncementsFeed } from '../components/AnnouncementsFeed';
import { GuardianProgressReport } from '../components/GuardianProgressReport';
import { Skeleton, PageHeader } from '../components/UI';

interface GuardianHomePageProps {
  org: Organization | null;
}

export function GuardianHomePage({ org }: GuardianHomePageProps) {
  const navigate = useNavigate();
  const [links, setLinks] = useState<FamilyLink[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (org) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [org]);

  async function loadData() {
    if (!org) return;
    setLoading(true);
    try {
      const [linksData, challengesData] = await Promise.all([
        listFamilyLinks(org.org_id),
        listChallenges(org.org_id).catch(() => ({ challenges: [] })),
      ]);
      const approvedLinks = linksData.links?.filter((l) => l.status === 'APPROVED') || [];
      setLinks(approvedLinks);

      // Filter challenges to only those where linked athletes are participating
      const athleteIds = approvedLinks.map((l) => l.athlete_id);
      const relevantChallenges = (challengesData.challenges || []).filter((c) =>
        c.participant_ids.some((pid) => athleteIds.includes(pid))
      );
      setChallenges(relevantChallenges);
    } catch (err) {
      console.error('Failed to load guardian data:', err);
    } finally {
      setLoading(false);
    }
  }

  // Get athlete challenge stats
  const athleteIds = links.map((l) => l.athlete_id);
  const activeChallenges = challenges.filter((c) => c.status === 'ACTIVE');
  // Find challenges where the linked athlete actually won (raffle winner)
  const wonChallenges = challenges.filter(
    (c) => c.status === 'COMPLETED' && c.raffle_winner_id && athleteIds.includes(c.raffle_winner_id)
  );

  if (loading) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <Skeleton height={36} width="50%" style={{ marginBottom: 8 }} />
        <Skeleton height={20} width="65%" style={{ marginBottom: 32 }} />
        <Skeleton.Card count={2} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Welcome Header */}
      <PageHeader
        title="Welcome, Parent"
        description="Stay connected with your athlete's training progress"
        icon={Heart}
        iconColor="var(--purple-600)"
      />

      {/* Info Banner */}
      <div
        className="card mb-6"
        style={{
          background:
            'linear-gradient(135deg, rgba(var(--purple-rgb), 0.08) 0%, rgba(168, 85, 247, 0.04) 100%)',
          border: '1px solid rgba(var(--purple-rgb), 0.15)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div
            className="icon-container icon-container-md"
            style={{ background: 'rgba(var(--purple-rgb), 0.15)' }}
          >
            <Users size={22} style={{ color: 'var(--purple-600)' }} />
          </div>
          <div>
            <h3
              style={{
                fontWeight: '600',
                color: 'var(--gray-900)',
                marginBottom: '4px',
                fontSize: '15px',
              }}
            >
              Guardian Access
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--gray-600)' }}>
              View your athlete's training videos, track their progress, and stay informed about
              their development.
            </p>
          </div>
        </div>
      </div>

      {/* Announcements Feed — section-wrapped (SKOOL-33) */}
      <section className="page-section">
        <header className="page-section-header">
          <p className="page-section-label">UPDATES</p>
          <h2 className="page-section-title">Announcements</h2>
          <div className="page-section-rule" aria-hidden="true" />
        </header>
        <AnnouncementsFeed trigger="login" maxItems={3} />
      </section>

      {/* AI Progress Report — section-wrapped (SKOOL-33) */}
      {!loading && links.length > 0 && (
        <section className="page-section">
          <header className="page-section-header">
            <p className="page-section-label">PROGRESS REPORT</p>
            <h2 className="page-section-title">How your athletes are doing</h2>
            <div className="page-section-rule" aria-hidden="true" />
          </header>
          <GuardianProgressReport />
        </section>
      )}

      {/* Challenge Win Celebration */}
      {!loading && wonChallenges.length > 0 && (
        <div
          className="card"
          style={{
            marginBottom: '32px',
            padding: '20px',
            background:
              'linear-gradient(135deg, rgba(var(--green-rgb), 0.1) 0%, rgba(var(--green-rgb), 0.05) 100%)',
            border: '2px solid rgba(var(--green-rgb), 0.3)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--color-success) 0%, #16a34a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(var(--green-rgb), 0.3)',
              }}
            >
              <Star size={28} style={{ color: 'white' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h3
                style={{
                  fontWeight: '700',
                  color: 'var(--color-success)',
                  fontSize: '18px',
                  marginBottom: '6px',
                }}
              >
                Champion Alert!
              </h3>
              <p style={{ fontSize: '15px', color: 'var(--gray-700)', marginBottom: '12px' }}>
                {links.length === 1 ? links[0].athlete_name : 'Your athlete'} won{' '}
                <strong>
                  {wonChallenges.length === 1
                    ? wonChallenges[0].title
                    : `${wonChallenges.length} challenges`}
                </strong>
                !
              </p>
              {wonChallenges[0].prize_description && (
                <p style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                  Prize: {wonChallenges[0].prize_description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Athlete Challenges Card */}
      {!loading && links.length > 0 && activeChallenges.length > 0 && (
        <div
          className="card"
          style={{
            marginBottom: '32px',
            padding: '20px',
            background:
              'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(251, 191, 36, 0.04) 100%)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'rgba(245, 158, 11, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Trophy size={24} style={{ color: '#f59e0b' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h3
                style={{
                  fontWeight: '600',
                  color: 'var(--gray-900)',
                  fontSize: '16px',
                  marginBottom: '6px',
                }}
              >
                <Flame
                  size={16}
                  style={{
                    display: 'inline',
                    verticalAlign: 'middle',
                    marginRight: '6px',
                    color: '#f59e0b',
                  }}
                />
                Competition mode is ON!
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--gray-600)', marginBottom: '12px' }}>
                {links.length === 1 ? links[0].athlete_name : 'Your athletes'}{' '}
                {links.length === 1 ? 'is' : 'are'} battling it out in{' '}
                <strong>
                  {activeChallenges.length} challenge{activeChallenges.length !== 1 ? 's' : ''}
                </strong>{' '}
                right now
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {activeChallenges.slice(0, 3).map((challenge) => (
                  <span
                    key={challenge.challenge_id}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(245, 158, 11, 0.1)',
                      borderRadius: '16px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#b45309',
                    }}
                  >
                    {challenge.title}
                  </span>
                ))}
                {activeChallenges.length > 3 && (
                  <span
                    style={{
                      padding: '6px 12px',
                      background: 'var(--gray-100)',
                      borderRadius: '16px',
                      fontSize: '13px',
                      color: 'var(--gray-600)',
                    }}
                  >
                    +{activeChallenges.length - 3} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Athletes Section — section-wrapped (SKOOL-33) */}
      <section className="page-section">
        <header className="page-section-header">
          <p className="page-section-label">YOUR ATHLETES</p>
          <h2 className="page-section-title">
            {links.length === 0 ? 'No linked athletes yet' : 'Athletes you manage'}
          </h2>
          {links.length === 0 && (
            <p className="page-section-description">
              Your athlete's coach will send you an invite. Check your email for the link.
            </p>
          )}
          <div className="page-section-rule" aria-hidden="true" />
        </header>
        {links.length === 0 ? (
          <EmptyState
            icon={<UserPlus />}
            title="No athletes linked yet"
            description="Your athlete's coach will send you an invite to connect. Check your email for the invite link."
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
              gap: '16px',
            }}
          >
            {links.map((link) => (
              <AthleteCard key={link.link_id} link={link} />
            ))}
          </div>
        )}
      </section>

      {/* Quick Actions — section-wrapped (SKOOL-33) */}
      {links.length > 0 && (
        <section className="page-section">
          <header className="page-section-header">
            <p className="page-section-label">QUICK ACTIONS</p>
            <h2 className="page-section-title">Where to next?</h2>
            <div className="page-section-rule" aria-hidden="true" />
          </header>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
            }}
          >
            <QuickActionCard
              icon={Video}
              title="View Videos"
              description="Watch training videos"
              onClick={() => navigate('/videos')}
            />
            <QuickActionCard
              icon={Bell}
              title="Notifications"
              description="Manage email alerts"
              onClick={() => navigate('/family')}
            />
            <QuickActionCard
              icon={Users}
              title="My Athletes"
              description="Manage linked athletes"
              onClick={() => navigate('/family')}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function AthleteCard({ link }: { link: FamilyLink }) {
  const navigate = useNavigate();

  return (
    <article className="page-featured" style={{ marginBottom: 0 }}>
      <div className="page-featured-strip">{(link.athlete_name || 'ATHLETE').toUpperCase()}</div>
      <div className="page-featured-body">
        <h3 className="page-featured-title">{link.athlete_name || 'Athlete'}</h3>
        <p className="page-featured-subtitle">
          {link.relationship
            ? link.relationship.charAt(0).toUpperCase() + link.relationship.slice(1).toLowerCase()
            : 'Linked athlete'}
        </p>
        <div className="page-featured-actions">
          <button
            type="button"
            className="page-featured-btn primary"
            onClick={() => navigate('/videos')}
          >
            View videos
          </button>
        </div>
      </div>
    </article>
  );
}

function QuickActionCard({
  icon: Icon,
  title,
  description,
  onClick,
}: {
  icon: typeof Video;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="card card-interactive"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        textAlign: 'left',
        width: '100%',
        border: 'none',
      }}
    >
      <div
        className="icon-container icon-container-sm"
        style={{ background: 'rgba(var(--purple-rgb), 0.1)' }}
      >
        <Icon size={18} style={{ color: 'var(--purple-600)' }} />
      </div>
      <div>
        <h4 style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '14px' }}>{title}</h4>
        <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
          {description}
        </p>
      </div>
      <ChevronRight
        size={16}
        style={{ color: 'var(--gray-400)', marginLeft: 'auto', flexShrink: 0 }}
      />
    </button>
  );
}
